import { useFormContext, Controller, Path } from "react-hook-form";
import { useMemo, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import Combobox from "@/components/ui/common/combobox";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { useGetAllMemberQuery, useGetMemberByIdQuery } from "@/services/memberApi";
import { differenceInYears, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { useGetQuestionsQuery, useGetConsultationAnswersQuery, useSubmitConsultationAnswersMutation, useCreateConsultationMutation, useUpdateConsultationMutation } from "@/services/consultationApi";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import DynamicQuestionRenderer from "./dynamic-question-renderer";
import { Consultation, FormQuestionResponse, ConsultationAnswer, ConsultationAnswerResponse, HistoryConsultation } from "@/types/consultation";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "@radix-ui/react-icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { FormControl } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { displayDate } from "@/lib/helper";
import { toast } from "@/components/ui/use-toast";
import { HistoryFilter } from "../history-filter";

interface FormStepProps {
  action: "add" | "edit";
  open: boolean;
  setAction?: (action: "add" | "edit") => void;
  refetch?: () => void;
  openSections?: string[];
  setOpenSections?: (sections: string[]) => void;
}

const Background = ({ action, open, setAction, refetch, openSections, setOpenSections }: FormStepProps) => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const {
    control,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    trigger,
    register,
    getValues,
  } = useFormContext<Consultation>();

  const watcher = watch();

  // Fetch members
  const { data: memberData } = useGetAllMemberQuery({
    query: "offset=0&sort_key=id&sort_order=desc&facility_type=consultation",
    org_id: orgId,
  }, { skip: !open });

  // Create member options
  const memberOptions = useMemo(() => {
    if (!Array.isArray(memberData?.data)) {
      return [];
    }
    return memberData?.data.map((member) => {
      const firstName = (member?.first_name || "").trim();
      const lastName = (member?.last_name || "").trim();
      const fullName = [firstName, lastName].filter(Boolean).join(" ");

      const labelPrefix = member.own_member_id
        ? `${member.own_member_id} • `
        : "";
      const label = `${labelPrefix}${fullName}`;

      return {
        label: label,
        value: member.id?.toString() || "",
      };
    });
  }, [memberData]);

  const selectedMember = useMemo(() => {
    if (!watcher.member_id || !memberData?.data) return null;
    return memberData.data.find((m) => m.id === Number(watcher.member_id));
  }, [watcher.member_id, memberData]);

  // Fetch full member detail to get accurate coaches list
  const { data: memberDetail } = useGetMemberByIdQuery(
    { org_id: orgId, id: Number(watcher.member_id) },
    { skip: !watcher.member_id || !open }
  );

  const membershipOptions = useMemo(() => {
    const plans = selectedMember?.membership_plans ?? [];

    const uniquePlans = Array.from(
      new Map(
        plans.map((plan) => [
          plan.membership_plan_id,
          {
            value: plan.membership_plan_id?.toString() ?? "",
            label: plan.membership_plan_name?.toString() ?? "",
          },
        ])
      ).values()
    );

    return uniquePlans;
  }, [selectedMember]);

  // Create coach options — only coaches assigned to the selected member (from full detail)
  const coachOptions = useMemo(() => {
    // getMemberById has no transformResponse so raw field may be coach_name or name
    const rawCoaches = (memberDetail?.coaches ?? selectedMember?.coaches ?? []) as Array<{
      id: number;
      name?: string;
      coach_name?: string;
    }>;
    return rawCoaches
      .map((c) => ({ id: c.id, name: c.name || c.coach_name || "" }))
      .filter((c) => c.id && c.name.trim())
      .map((c) => ({
        label: c.name,
        value: c.id.toString(),
      }));
  }, [memberDetail, selectedMember]);

  // Fetch questions
  const { data: questionsData } = useGetQuestionsQuery({
    org_id: orgId,
    section_key: ["medical", "nutrition", "exercise"],
  }, { skip: !open });

  const groupedQuestions = useMemo<Record<string, FormQuestionResponse[]>>(() => {
    return questionsData?.data || {};
  }, [questionsData]);

  const consultationId = watcher.id;
  
  const calculatedAge = useMemo(() => {
    if (!watcher.member_dob) return 0;
    try {
      return differenceInYears(new Date(), parseISO(watcher.member_dob));
    } catch (e) {
      return 0;
    }
  }, [watcher.member_dob]);

  const { data: answersData } = useGetConsultationAnswersQuery(
    { consultation_id: Number(consultationId), org_id: orgId, section_key: ["medical", "nutrition", "exercise"] },
    { skip: action !== "edit" || !consultationId || !open }
  );
  console.log({answersData})

  useEffect(() => {
    if (action === "edit" && answersData?.data) {
      Object.values(answersData.data).forEach((sectionAnswers: ConsultationAnswerResponse[]) => {
        if (Array.isArray(sectionAnswers)) {
          sectionAnswers.forEach((answer: ConsultationAnswerResponse) => {
            if (answer.question_id && answer.answer !== null) {
              setValue(`answers.${answer.question_id}`, answer.answer);
            }
          });
        }
      });
    }
  }, [answersData, action, setValue]);

  useEffect(() => {
    if (!watcher.member_id) {
      setValue("member_name", "");
      setValue("member_dob", "");
      setValue("member_gender", "");
      setValue("member_email", "");
      setValue("member_height", "");
      setValue("member_weight", "");
      setValue("membership_id", undefined);
      setValue("coach_id", undefined);
      setValue("coach_name", "");
      return;
    }

    if (selectedMember) {
      const firstName = (selectedMember?.first_name || "").trim();
      const lastName = (selectedMember?.last_name || "").trim();
      const fullName = [firstName, lastName].filter(Boolean).join(" ");
      
      setValue("member_name", fullName);
      const dobValue = selectedMember.dob instanceof Date 
        ? selectedMember.dob.toISOString() 
        : selectedMember.dob;
      setValue("member_dob", dobValue || "");
      setValue("member_gender", selectedMember.gender || "");
      setValue("member_email", selectedMember.email || "");

      // Only set height/weight if the member actually has a recorded (non-zero) value
      // so the field stays blank (and triggers the required prompt) when the member has none
      if (!getValues("member_height")) {
        setValue(
          "member_height",
          selectedMember.height && Number(selectedMember.height) !== 0
            ? selectedMember.height
            : ""
        );
      }
      if (!getValues("member_weight")) {
        setValue(
          "member_weight",
          selectedMember.weight && Number(selectedMember.weight) !== 0
            ? selectedMember.weight
            : ""
        );
      }

      // Auto-select coach ONLY if not already set (e.g., in add mode)
      const rawCoaches = (memberDetail?.coaches ?? selectedMember?.coaches ?? []) as Array<{
        id: number;
        name?: string;
        coach_name?: string;
      }>;
      const assignedCoaches = rawCoaches
        .map((c) => ({ id: c.id, name: c.name || c.coach_name || "" }))
        .filter((c) => c.id && c.name.trim());

      if (!getValues("coach_id")) {
        if (assignedCoaches.length === 1) {
          setValue("coach_id", assignedCoaches[0].id);
          setValue("coach_name", assignedCoaches[0].name);
        }
      }

      // Auto-select membership if the member has exactly one plan
      if (!getValues("membership_id")) {
        if (membershipOptions.length === 1) {
          setValue("membership_id", Number(membershipOptions[0].value));
        }
      }
    }
  }, [watcher.member_id, selectedMember, memberDetail, membershipOptions, setValue]);

  const [submitConsultationAnswers] = useSubmitConsultationAnswersMutation();
  const [savingSection, setSavingSection] = useState<string | null>(null);
  const [memberResetKey, setMemberResetKey] = useState(0);
  const [historyData, setHistoryData] = useState<HistoryConsultation | null>(null);

  const handleSaveSection = async (section: string, questions: FormQuestionResponse[]) => {
    if (!consultationId) {
      toast({
        variant: "destructive",
        description: "Please use the Save button at the top to create consultation first",
      });
      return;
    }

    // Validate required fields in this section
    const questionNames = questions.map(q => `answers.${q.id}` as Path<Consultation>);
    const isSectionValid = await trigger(questionNames);

    if (!isSectionValid) {
      toast({
        variant: "destructive",
        description: "Please fill all mandatory fields in this section",
      });
      return;
    }

    const answersPayload: ConsultationAnswer[] = [];
    questions.forEach((q) => {
      const value = watcher.answers?.[String(q.id)];
      if (value !== undefined && value !== null && value !== "") {
        answersPayload.push({
          question_id: q.id,
          question_type: q.question_type,
          answer: value,
        });
      }
    });

    if (answersPayload.length === 0) {
      toast({
        description: "No answers to save in this section",
      });
      return;
    }

    setSavingSection(section);
    try {
      await submitConsultationAnswers({
        consultation_id: Number(consultationId),
        org_id: orgId,
        answers: answersPayload,
      }).unwrap();

      // NO TOAST HERE - Intermediate sections don't show success messages
      // Only the main Save button shows toasts
    } catch (error: unknown) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save section answers",
      });
    } finally {
      setSavingSection(null);
    }
  };

  // const getSectionProgress = (questions: FormQuestionResponse[]) => {
  //   if (!questions?.length) return { filled: 0, total: 0 };
  //   const total = questions.length;
  //   const filled = questions.filter((q) => {
  //     const answer = watcher.answers?.[String(q.id)];
  //     if (Array.isArray(answer)) return answer.length > 0;
  //     if (q.question_type === "toggle") {
  //       return answer === true || answer === "true" || answer === 1;
  //     }
  //     return answer !== undefined && answer !== null && answer !== "";
  //   }).length;
  //   return { filled, total };
  // };

  // const isSectionRequiredFilled = (questions: FormQuestionResponse[]) => {
  //   const requiredQuestions = questions.filter((q) => q.is_required);
  //   if (requiredQuestions.length === 0) return true;
    
  //   return requiredQuestions.every((q) => {
  //     const answer = watcher.answers?.[String(q.id)];
  //     if (Array.isArray(answer)) return answer.length > 0;
  //     if (q.question_type === "toggle") {
  //       return answer === true || answer === "true" || answer === 1;
  //     }
  //     return answer !== undefined && answer !== null && answer !== "";
  //   });
  // };

  return (
    <div className="space-y-4">
      {/* <HistoryFilter memberId={watcher.member_id!} onDataFound={setHistoryData} /> */}

      <Card className="p-4">
        <div className="flex items-center justify-between">
            <p className="font-medium text-sm">Consultation Details</p>
        </div>
        <Separator className="h-[1px] mt-2" />
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          {/* Member Selection */}
          <div>
            <Controller
              name="member_id"
              control={control}
              rules={{ validate: (v) => (v !== undefined && v !== null && v !== 0) || "Member is required" }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <>
                  <p className="text-sm mb-2">Member <span className="text-red-500">*</span></p>
                  <Combobox
                    key={`member-combobox-${value}-${memberResetKey}`}
                    list={memberOptions}
                    defaultValue={
                      memberOptions.find(
                        (option) => option.value === value?.toString()
                      )?.label || ""
                    }
                    setFilter={(selectedLabel: string) => {
                      const selectedOption = memberOptions.find(
                        (option) => option.label === selectedLabel
                      );
                      if (selectedOption) {
                        onChange(Number(selectedOption.value));
                      } else {
                        onChange(undefined);
                      }
                    }}
                    label="Member"
                    showLabel={false}
                    disabled={action === "edit"}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>

          {/* Membership */}
          <div>
            <Controller
              name="membership_id"
              control={control}
              rules={{ validate: (v) => (v !== undefined && v !== null && v !== 0) || "Membership is required" }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <p className="text-sm mb-2">Membership <span className="text-red-500">*</span></p>
                  <Combobox
                    list={membershipOptions}
                    defaultValue={
                      membershipOptions.find(
                        (option) => option.value === value?.toString()
                      )?.label || ""
                    }
                    setFilter={(selectedLabel: string) => {
                      const selectedPlan = membershipOptions.find(
                        (plan) => plan.label === selectedLabel
                      );

                      if (selectedPlan) {
                        onChange(Number(selectedPlan.value));
                      } else {
                        onChange(undefined);
                      }
                    }}
                    className="w-full"
                    disabled={!selectedMember}
                    label="Membership"
                    showLabel={false}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>

          {/* Coach Selection */}
          <div>
            <Controller
              name="coach_id"
              control={control}
              rules={{ validate: (v) => (v !== undefined && v !== null && v !== 0) || "Coach is required" }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <>
                  <p className="text-sm mb-2">Coach <span className="text-red-500">*</span></p>
                  <Combobox
                    key={`coach-combobox-${watcher.member_id}-${value}`}
                    list={coachOptions}
                    defaultValue={
                      coachOptions.find(
                        (option) => option.value === value?.toString()
                      )?.label || ""
                    }
                    setFilter={(selectedLabel: string) => {
                      const selectedOption = coachOptions.find(
                        (option) => option.label === selectedLabel
                      );
                      if (selectedOption) {
                        onChange(Number(selectedOption.value));
                        setValue("coach_name", selectedOption.label);
                      } else {
                        onChange(undefined);
                        setValue("coach_name", "");
                      }
                    }}
                    label="Coach"
                    showLabel={false}
                    disabled={!selectedMember}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>

          {/* Date Selection */}
          <div>
            <Controller
              name="consultation_date"
              control={control}
              rules={{ required: "Date is required" }}
              render={({
                field: { value },
                fieldState: { error },
              }) => (
                <>
                  <p className="text-sm mb-2">Date <span className="text-red-500">*</span></p>
                  <div className="flex items-center gap-2">
                    {/* Date is read-only on edit — the Popover trigger is omitted so it can't be changed */}
                    {action === "edit" ? (
                      <Button
                        variant={"outline"}
                        type="button"
                        disabled
                        className={cn(
                          "w-full pl-3 text-gray-800 text-left font-normal border-gray-300 border-[1px] cursor-not-allowed opacity-100"
                        )}
                      >
                        {value ? (
                          displayDate(new Date(value))
                        ) : (
                          <span>Select date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    ) : (
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <div className="relative flex-1">
                              <Button
                                variant={"outline"}
                                type="button"
                                className={cn(
                                  "w-full pl-3 text-gray-800 text-left font-normal border-gray-300 border-[1px]"
                                )}
                              >
                                {value ? (
                                  displayDate(new Date(value))
                                ) : (
                                  <span>Select date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </div>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={value ? new Date(value) : undefined}
                            onSelect={(date) => {
                              if (date) {
                                setValue("consultation_date", date.toISOString());
                              }
                            }}
                            disabled={(date) =>
                              date > new Date() || date < new Date("1900-01-01")
                            }
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>

          {/* Gender */}
          <div className="w-full">
             <p className="text-sm mb-2 opacity-0">Gender</p>
             <FloatingLabelInput
                label="Gender"
                disabled
                {...register("member_gender")}
             />
          </div>

          {/* Email */}
          <div className="w-full">
            <p className="text-sm mb-2 opacity-0">Email</p>
             <FloatingLabelInput
                label="Email"
                disabled
                {...register("member_email")}
             />
          </div>

          {/* Age */}
          <div className="w-full">
            <p className="text-sm mb-2 opacity-0">Age</p>
             <FloatingLabelInput
                label="Age"
                disabled
                value={calculatedAge || ""}
                onChange={() => {}}
             />
          </div>

          {/* Height */}
          <div className="w-full">
            <p className="text-sm mb-2 opacity-0">Height</p>
             <FloatingLabelInput
                label="Height (cm)"
                type="number"
                min={0}
                text="*"
                error={errors.member_height?.message as string | undefined}
                {...register("member_height", { required: "Height is required" })}
             />
          </div>

          {/* Weight */}
          <div className="w-full">
            <p className="text-sm mb-2 opacity-0">Weight</p>
             <FloatingLabelInput
                label="Weight (kg)"
                type="number"
                min={0}
                text="*"
                error={errors.member_weight?.message as string | undefined}
                {...register("member_weight", { required: "Weight is required" })}
             />
          </div>

          {/* REMOVED: Create Button - Now using Save button in header */}
        </div>
      </Card>

      <Accordion
        type="multiple"
        className="w-full space-y-3"
        value={openSections}
        onValueChange={setOpenSections}
      >
        {(["medical", "nutrition", "exercise"] as const)
          .filter((key) => key in groupedQuestions)
          .map((section) => {
            const questions = groupedQuestions[section] ?? [];
            // const { filled, total } = getSectionProgress(questions);
            const sectionTitle = section.charAt(0).toUpperCase() + section.slice(1);

            const sectionSubtitles: Record<string, string> = {
              medical: "Health history & clearance",
              nutrition: "Eating habits & intake",
              exercise: "Training history & capacity",
            };
            const subtitle = sectionSubtitles[section] || "";

            const isOpen = openSections?.includes(section) ?? false;

            const isFullWidth = (q: FormQuestionResponse) => {
              if (q.question_type === "textarea") return true;
              if (
                (q.question_type === "radio" || q.question_type === "select") &&
                (q.options?.length ?? 0) > 3
              )
                return true;
              return false;
            };

            return (
              <AccordionItem
                key={section}
                value={section}
                className="border border-gray-200 rounded-xl bg-white overflow-hidden"
              >
                <AccordionTrigger className="hover:no-underline px-5 py-4 bg-white hover:bg-white [&[data-state=open]]:bg-white focus-visible:ring-0 focus-visible:outline-none">
                  <div className="flex items-center justify-between w-full pr-2">
                    <div className="text-left">
                      <p className="font-semibold text-gray-800 text-sm">{sectionTitle}</p>
                      {subtitle && (
                        <p className="text-xs text-gray-400 font-normal mt-0.5">{subtitle}</p>
                      )}
                    </div>
                    {/* <Badge
                      className={cn(
                        "font-semibold rounded-full px-3 py-0.5 text-xs text-white border-0 transition-colors",
                        isOpen ? "bg-green-500 hover:bg-green-500" : "bg-primary hover:bg-primary"
                      )}
                    >
                      {filled}/{total}
                    </Badge> */}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-5 px-4">
                  <Separator className="mb-4" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {questions.map((q, qIdx) =>
                      isFullWidth(q) ? (
                        <div key={q.id} className="col-span-1 md:col-span-2">
                          <DynamicQuestionRenderer
                            question={q}
                            control={control}
                            index={qIdx + 1}
                          />
                        </div>
                      ) : (
                        <DynamicQuestionRenderer
                          key={q.id}
                          question={q}
                          control={control}
                          index={qIdx + 1}
                        />
                      )
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
      </Accordion>
    </div>
  );
};

export default Background;